/**
 * Developer Mode Utility
 * Handles detection of developer mode from URL parameters
 * 
 * Note: As of v3.6.0, Crown Jewels is generally available to all users
 */

/**
 * Check if developer mode is enabled via URL parameter
 * @returns {boolean} True if ?dev=true is present in URL
 */
export const isDeveloperMode = () => {
  if (typeof window === 'undefined') return false;
  
  const urlParams = new URLSearchParams(window.location.search);
  const devParam = urlParams.get('dev');
  
  // Check for dev=true (case-insensitive)
  return devParam && devParam.toLowerCase() === 'true';
};

/**
 * Check if internal mode is enabled via URL parameter
 * @returns {boolean} True if ?internal=true is present in URL
 */
export const isInternalMode = () => {
  if (typeof window === 'undefined') return false;
  
  const urlParams = new URLSearchParams(window.location.search);
  const internalParam = urlParams.get('internal');
  
  // Check for internal=true (case-insensitive)
  return internalParam && internalParam.toLowerCase() === 'true';
};

/**
 * Get developer mode status with caching for performance
 * Caches result during page session to avoid repeated URL parsing
 * 
 * Note: Crown Jewels module is now generally available (v3.6.0+)
 */
let cachedDeveloperMode = null;

export const getDeveloperMode = () => {
  if (cachedDeveloperMode === null) {
    cachedDeveloperMode = isDeveloperMode();
    
    // Log developer mode status for debugging
    if (cachedDeveloperMode) {
      console.log('🔧 Developer mode enabled via URL parameter');
    }
  }
  
  return cachedDeveloperMode;
};

/**
 * Get internal mode status with caching for performance
 * Caches result during page session to avoid repeated URL parsing
 */
let cachedInternalMode = null;

export const getInternalMode = () => {
  if (cachedInternalMode === null) {
    cachedInternalMode = isInternalMode();
    
    // Log internal mode status for debugging
    if (cachedInternalMode) {
      console.log('🔒 Internal mode enabled via URL parameter');
    }
  }
  
  return cachedInternalMode;
};

/**
 * Check if Crown Jewels module is enabled
 * @returns {boolean} Always true as of v3.6.0 (general availability)
 */
export const isCrownJewelsEnabled = () => {
  // Crown Jewels is now generally available to all users
  return true;
};

/**
 * Check if Security Services module is enabled
 * @returns {boolean} True if internal mode is enabled
 */
export const isSecurityServicesEnabled = () => {
  return getInternalMode();
};

/**
 * Reset cached modes (useful for testing)
 */
export const resetModeCache = () => {
  cachedDeveloperMode = null;
  cachedInternalMode = null;
}; 